import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Sparkles, Flame, Scissors } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

// Your actual before/after gallery images
const GALLERY_ITEMS = [
  {
    id: 1,
    category: "Body Contouring",
    title: "Booty Lift Before & After",
    description: "Incredible lifting and smoothing results with our vacuum therapy and body sculpting sessions",
    image: "/gallery/contour1.jpg",
    icon: Flame
  },
  {
    id: 2,
    category: "Body Contouring",
    title: "Body Sculpting Results",
    description: "Before and after body contouring — visible toning and shaping with wood therapy",
    image: "/gallery/contour2.jpg",
    icon: Flame
  },
  {
    id: 3,
    category: "Body Contouring",
    title: "Contouring Transformation",
    description: "Real client results from our body contouring and vacuum therapy treatments",
    image: "/gallery/contour3.jpg",
    icon: Flame
  },
  {
    id: 10,
    category: "Waxing",
    title: "Underarm Wax Before & After",
    description: "Clean, smooth results with our professional underarm waxing service",
    image: "/gallery/waxing1.jpg",
    icon: Scissors
  },
  {
    id: 11,
    category: "Waxing",
    title: "Back Wax Results",
    description: "Before and after back wax — smooth, hair-free skin in one session",
    image: "/gallery/waxing2.jpg",
    icon: Scissors
  },
  {
    id: 12,
    category: "Waxing",
    title: "Face Wax in Progress",
    description: "Professional facial waxing for a clean, polished look",
    image: "/gallery/waxing3.jpg",
    icon: Scissors
  },
  {
    id: 13,
    category: "Waxing",
    title: "Lip Wax Results",
    description: "Smooth, flawless upper lip after our gentle waxing treatment",
    image: "/gallery/waxing4.jpg",
    icon: Scissors
  },
  {
    id: 14,
    category: "Facial Treatment",
    title: "Glowing Skin Before & After",
    description: "Visible improvement in skin texture and radiance after our signature facial",
    image: "/gallery/facial1.jpg",
    icon: Sparkles
  },
  {
    id: 15,
    category: "Facial Treatment",
    title: "Brightening Facial Results",
    description: "Even skin tone and a healthy glow after our brightening treatment",
    image: "/gallery/facial2.jpg",
    icon: Sparkles
  },
  {
    id: 16,
    category: "Facial Treatment",
    title: "Relaxing Facial Session",
    description: "Client enjoying a rejuvenating facial treatment at Glamour Body",
    image: "/gallery/facial3.jpg",
    icon: Sparkles
  },
  {
    id: 17,
    category: "Facial Treatment",
    title: "Post-Facial Glow",
    description: "Beautiful, refreshed skin after a customized facial treatment",
    image: "/gallery/facial4.jpg",
    icon: Sparkles
  }
];

const CATEGORIES = ["All", "Body Contouring", "Facial Treatment", "Waxing"];

const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredItems = selectedCategory === "All" 
    ? GALLERY_ITEMS 
    : GALLERY_ITEMS.filter(item => item.category === selectedCategory);

  const currentIndex = selectedImage ? filteredItems.findIndex(item => item.id === selectedImage.id) : -1;

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setSelectedImage(filteredItems[currentIndex - 1]);
    }
  };

  const goToNext = () => {
    if (currentIndex < filteredItems.length - 1) {
      setSelectedImage(filteredItems[currentIndex + 1]);
    }
  };

  return (
    <>
      <SEO 
        title="Gallery - Before & After Results"
        description="View stunning before and after transformations at Glamour Body. See real results from our body contouring, facial treatments, and waxing services in Katy, Texas."
        keywords="before after results Katy Texas, body contouring transformation, facial treatment results, beauty salon gallery Houston, Glamour Body results"
        canonical="https://glamourbodyhouston.com/gallery"
      />
      
      <div className="pt-24 min-h-screen">
        <section className="relative py-16 px-4" data-testid="gallery-section">
          <div className="orb-green top-20 -left-40" />
          <div className="orb-purple top-60 -right-60" />
          <div className="orb-teal bottom-40 left-20" />
          <div className="orb-cyan bottom-20 -right-40" />
          
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="font-italiana text-4xl sm:text-5xl text-white mb-4 tracking-wider uppercase">
                Results Gallery
              </h1>
              <p className="text-white/60 text-base lg:text-lg max-w-2xl mx-auto">
                See the amazing transformations our clients have achieved with our premium treatments
              </p>
            </motion.div>
            
            {/* Category Filter */}
            <motion.div 
              className="flex flex-wrap justify-center gap-3 mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {CATEGORIES.map((category) => (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  variant={selectedCategory === category ? "default" : "outline"}
                  className={`rounded-full px-6 ${
                    selectedCategory === category 
                      ? "btn-primary" 
                      : "btn-secondary"
                  }`}
                  data-testid={`filter-${category.toLowerCase().replace(" ", "-")}`}
                >
                  {category}
                </Button>
              ))}
            </motion.div>
            
            {/* Gallery Grid */}
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              layout
            >
              <AnimatePresence mode="popLayout">
                {filteredItems.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <motion.div
                      key={item.id}
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ delay: index * 0.05 }}
                      className="group cursor-pointer"
                      onClick={() => setSelectedImage(item)}
                      data-testid={`gallery-item-${item.id}`}
                    >
                      <div className="glass-card rounded-2xl overflow-hidden">
                        <div className="relative aspect-square overflow-hidden">
                          <img 
                            src={item.image} 
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                            <div className="flex items-center gap-2 mb-1">
                              <IconComponent size={16} className="text-cyan-400" />
                              <span className="text-cyan-400 text-xs uppercase tracking-wide">{item.category}</span>
                            </div>
                            <h3 className="text-white font-semibold">{item.title}</h3>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
            
            {/* CTA Section */}
            <motion.div 
              className="text-center mt-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-white/60 mb-6">Ready to start your transformation?</p>
              <Link to="/contact">
                <Button className="btn-primary rounded-full px-8 py-4">
                  Book Your Appointment
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
        
        {/* Lightbox Modal */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox"
            >
              <button 
                className="absolute top-4 right-4 text-white/70 hover:text-white z-10"
                onClick={() => setSelectedImage(null)}
                aria-label="Close"
              >
                <X size={32} />
              </button>
              
              {currentIndex > 0 && (
                <button 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10 glass-card rounded-full p-2"
                  onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                  aria-label="Previous"
                >
                  <ChevronLeft size={32} />
                </button>
              )}
              
              {currentIndex < filteredItems.length - 1 && (
                <button 
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10 glass-card rounded-full p-2"
                  onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  aria-label="Next"
                >
                  <ChevronRight size={32} />
                </button>
              )}
              
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="max-w-4xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img 
                  src={selectedImage.image} 
                  alt={selectedImage.title}
                  className="w-full rounded-2xl max-h-[70vh] object-contain mx-auto"
                />
                <div className="mt-4 text-center">
                  <span className="text-cyan-400 text-sm uppercase tracking-wide">{selectedImage.category}</span>
                  <h3 className="text-white text-xl font-italiana mt-1">{selectedImage.title}</h3>
                  <p className="text-white/60 mt-2">{selectedImage.description}</p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default GalleryPage;
